import React, { useEffect, useMemo, useRef, useState } from 'react';
import Badge from '../ui/Badge';
import ImagePicker from '../ImagePicker';
import { getMediaUrl } from '../api';
import Field, { CheckField } from './Field';
import AutoTextarea from './AutoTextarea';
import { CREATABLE_TYPES, getDefaultSectionData, getSectionPreview, isJsonEditedType, typeLabel } from './sectionTypes';
import SECTION_EDITORS from './sectionEditors';

/* The editable slice of a section. Everything else (id, order, timestamps) is
   passed through untouched when saving. */
const pick = (section) => ({
  type: section.type,
  data: section.data || {},
  settings: section.settings || {},
});

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/* Raw JSON editing for shapes the form does not model. Text is committed to
   the draft only when it parses; a parse error shows inline. */
function JsonField({ id, label, value, onChange, hint, rows = 8 }) {
  const serialize = (v) => JSON.stringify(v ?? {}, null, 2);
  const [text, setText] = useState(() => serialize(value));
  const [err, setErr] = useState(null);
  const committed = useRef(text);

  useEffect(() => {
    const s = serialize(value);
    if (s !== committed.current) {
      committed.current = s;
      setText(s);
      setErr(null);
    }
  }, [value]);

  const onText = (t) => {
    setText(t);
    try {
      const parsed = JSON.parse(t);
      committed.current = serialize(parsed);
      setErr(null);
      onChange(parsed);
    } catch (e) {
      setErr(`Invalid JSON: ${e.message}`);
    }
  };

  return (
    <Field id={id} label={label} hint={hint} error={err}>
      <textarea
        id={id}
        className={`textarea${err ? ' is-invalid' : ''}`}
        value={text}
        onChange={(e) => onText(e.target.value)}
        rows={rows}
        spellCheck={false}
        style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
        aria-invalid={!!err}
      />
    </Field>
  );
}

/**
 * One section rendered in place: header (position, type, preview, unsaved
 * marker, reorder/delete/collapse) and, when expanded, the inline form.
 * Edits live in a local draft until "Save section" calls onSave(section, draft).
 */
export default function SectionEditor({
  section,
  index,
  count,
  expanded,
  onToggle,
  onMove,
  onDelete,
  onSave,
  onDirtyChange,
  busy,
  savedVersion = 0,
}) {
  const sid = `section-${section.id}`;
  const [draft, setDraft] = useState(() => pick(section));
  // The library picker, when open: { onSelect } so any field -- the image
  // section, a gallery row, a list_item's image -- can claim the next pick.
  const [picker, setPicker] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Reset only after this section's own save lands, so a refetch caused by
  // reordering or saving a neighbour never clobbers unsaved edits here.
  useEffect(() => {
    setDraft(pick(section));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section.id, savedVersion]);

  const dirty = useMemo(() => !same(pick(section), draft), [section, draft]);

  useEffect(() => {
    onDirtyChange(section.id, dirty);
  }, [dirty, section.id, onDirtyChange]);
  useEffect(() => () => onDirtyChange(section.id, false), [section.id, onDirtyChange]);

  const data = draft.data || {};
  const settings = draft.settings || {};
  const setData = (field, value) => setDraft((d) => ({ ...d, data: { ...(d.data || {}), [field]: value } }));
  const setSettings = (field, value) => setDraft((d) => ({ ...d, settings: { ...(d.settings || {}), [field]: value } }));
  const setType = (type) => setDraft((d) => ({ ...d, type, data: getDefaultSectionData(type) }));

  // Editor-only thumbnail for an image section chosen from the library.
  const mediaId = draft.type === 'image' && !data.src ? data.media_id : null;
  useEffect(() => {
    if (!mediaId) {
      setPreviewUrl(null);
      return undefined;
    }
    let cancelled = false;
    getMediaUrl(mediaId)
      .then((url) => { if (!cancelled) setPreviewUrl(url); })
      .catch(() => { if (!cancelled) setPreviewUrl(null); });
    return () => { cancelled = true; };
  }, [mediaId]);

  /* Open the library picker for whichever field asked for it. */
  const openPicker = (onSelect) => setPicker({ onSelect });

  const onPicked = (image) => {
    if (picker && picker.onSelect) picker.onSelect(image);
    setPicker(null);
  };

  const pickForImageSection = () => openPicker((image) => setDraft((d) => ({
    ...d,
    data: {
      ...(d.data || {}),
      media_id: image._id,
      alt: (d.data && d.data.alt) || image.description || image.name || '',
    },
  })));

  const pickForGallery = () => openPicker((image) => setDraft((d) => ({
    ...d,
    data: {
      ...(d.data || {}),
      images: [...((d.data && d.data.images) || []), { media_id: image._id, src: '', alt: image.description || image.name || '', caption: '' }],
    },
  })));

  /* Replace the whole data object: the editors in sectionEditors/ spread what
     they were given, so keys this console does not model survive a save. */
  const setAllData = (next) => setDraft((d) => ({ ...d, data: next && typeof next === 'object' ? next : {} }));

  const hidden = settings.visible === false;
  const jsonEdited = isJsonEditedType(draft.type);
  const typeOptions = CREATABLE_TYPES.includes(draft.type) ? CREATABLE_TYPES : [draft.type, ...CREATABLE_TYPES];

  const text = (field, label, placeholder, extra = {}) => (
    <Field id={`${sid}-${field}`} label={label} {...extra}>
      <input id={`${sid}-${field}`} type="text" className="input" value={data[field] || ''} onChange={(e) => setData(field, e.target.value)} placeholder={placeholder} />
    </Field>
  );
  const select = (field, label, options, fallback) => (
    <Field id={`${sid}-${field}`} label={label}>
      <select id={`${sid}-${field}`} className="select" value={data[field] ?? fallback} onChange={(e) => setData(field, typeof fallback === 'number' ? parseInt(e.target.value, 10) : e.target.value)}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </Field>
  );

  const renderFields = () => {
    // Types with a richer form live in their own file.
    const Custom = SECTION_EDITORS[draft.type];
    if (Custom) {
      return <Custom idPrefix={sid} data={data} onChange={setAllData} onPickImage={openPicker} />;
    }
    switch (draft.type) {
      case 'markdown':
        return (
          <>
            <Field id={`${sid}-content`} label="Markdown">
              <AutoTextarea id={`${sid}-content`} value={data.content || ''} onChange={(e) => setData('content', e.target.value)} placeholder="Write in Markdown…" minRows={6} style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }} />
            </Field>
            <CheckField id={`${sid}-allow-html`} label="Allow HTML inside the Markdown" checked={data.allow_html} onChange={(v) => setData('allow_html', v)} />
          </>
        );
      case 'image':
        return (
          <>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 12 }}>
              {(data.src || previewUrl) && (
                <img src={data.src || previewUrl} alt={data.alt || ''} style={{ maxWidth: 200, maxHeight: 140, borderRadius: 'var(--radius)', border: '1px solid var(--color-border)' }} />
              )}
              <button type="button" className="btn btn-secondary btn-sm" onClick={pickForImageSection}>
                {data.media_id ? 'Change library image' : 'Choose from library'}
              </button>
            </div>
            {text('src', 'Image URL', 'https://…', { hint: 'Public URL the site renders. Choosing from the library fills the media ID and alt text.' })}
            {text('alt', 'Alt text', 'Describe the image')}
            {text('caption', 'Caption', 'Optional caption')}
            <div className="field-row">
              {select('alignment', 'Alignment', [['left', 'Left'], ['center', 'Center'], ['right', 'Right'], ['full', 'Full']], 'center')}
              {select('size', 'Size', [['small', 'Small'], ['medium', 'Medium'], ['large', 'Large'], ['full', 'Full width'], ['original', 'Original']], 'medium')}
            </div>
            <Field id={`${sid}-media_id`} label="Media ID">
              <input id={`${sid}-media_id`} type="text" className="input mono" value={data.media_id || ''} onChange={(e) => setData('media_id', e.target.value)} />
            </Field>
          </>
        );
      case 'youtube':
        return (
          <>
            {text('video_id', 'YouTube video ID', 'dQw4w9WgXcQ', { hint: 'The part after v= in the YouTube URL.' })}
            {text('title', 'Title', 'For accessibility')}
            <div className="field-row">
              <Field id={`${sid}-start_time`} label="Start time (seconds)">
                <input id={`${sid}-start_time`} type="number" min="0" className="input" value={data.start_time || 0} onChange={(e) => setData('start_time', parseInt(e.target.value, 10) || 0)} />
              </Field>
              {select('aspect_ratio', 'Aspect ratio', [['16:9', '16:9'], ['4:3', '4:3'], ['1:1', '1:1']], '16:9')}
            </div>
            <CheckField id={`${sid}-autoplay`} label="Autoplay" checked={data.autoplay} onChange={(v) => setData('autoplay', v)} />
          </>
        );
      case 'html':
        return (
          <>
            <Field id={`${sid}-content`} label="HTML">
              <AutoTextarea id={`${sid}-content`} value={data.content || ''} onChange={(e) => setData('content', e.target.value)} placeholder="<div>…</div>" minRows={6} spellCheck={false} style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }} />
            </Field>
            <CheckField id={`${sid}-sanitize`} label="Sanitize HTML (recommended)" checked={data.sanitize !== false} onChange={(v) => setData('sanitize', v)} />
          </>
        );
      case 'gallery':
        return (
          <>
            <div style={{ marginBottom: 12 }}>
              <button type="button" className="btn btn-secondary btn-sm" onClick={pickForGallery}>+ Add image from library</button>
            </div>
            <JsonField id={`${sid}-images`} label={`Images (${(data.images || []).length})`} value={data.images || []} onChange={(v) => setData('images', Array.isArray(v) ? v : [])} hint='Array of {"src", "alt", "caption", "media_id"}.' rows={6} />
            <div className="field-row">
              {select('layout', 'Layout', [['grid', 'Grid'], ['masonry', 'Masonry'], ['carousel', 'Carousel']], 'grid')}
              {select('columns', 'Columns', [[1, '1'], [2, '2'], [3, '3'], [4, '4'], [5, '5']], 3)}
            </div>
          </>
        );
      case 'quote':
        return (
          <>
            <Field id={`${sid}-quote`} label="Quote">
              <AutoTextarea id={`${sid}-quote`} value={data.quote || ''} onChange={(e) => setData('quote', e.target.value)} placeholder="The quoted text…" minRows={3} />
            </Field>
            <div className="field-row">
              {text('author', 'Author', 'Optional')}
              {text('source', 'Source', 'Optional')}
            </div>
            {select('alignment', 'Alignment', [['left', 'Left'], ['center', 'Center'], ['right', 'Right']], 'center')}
          </>
        );
      case 'divider':
        return (
          <>
            <div className="field-row">
              {select('style', 'Style', [['line', 'Line'], ['dashed', 'Dashed'], ['dotted', 'Dotted'], ['double', 'Double'], ['space', 'Space only']], 'line')}
              {select('thickness', 'Thickness', [['thin', 'Thin'], ['normal', 'Normal'], ['thick', 'Thick']], 'normal')}
            </div>
            {text('color', 'Color', '#333333, gray, transparent')}
          </>
        );
      case 'embed':
        return (
          <>
            <Field id={`${sid}-embed_code`} label="Embed code">
              <AutoTextarea id={`${sid}-embed_code`} value={data.embed_code || ''} onChange={(e) => setData('embed_code', e.target.value)} placeholder="<iframe …>" minRows={4} spellCheck={false} style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }} />
            </Field>
            {text('source', 'Source', 'Twitter, Instagram, CodePen…')}
            <div className="field-row">
              {text('width', 'Width', '100%')}
              {text('height', 'Height', 'auto')}
            </div>
          </>
        );
      default:
        return (
          <JsonField
            id={`${sid}-data`}
            label="Raw data (JSON)"
            value={data}
            onChange={setAllData}
            hint="Edited as JSON because the console has no form for this type."
            rows={12}
          />
        );
    }
  };

  return (
    <article
      id={sid}
      className="card"
      aria-labelledby={`${sid}-heading`}
      style={{ marginTop: 0, borderColor: dirty ? 'var(--color-warning)' : undefined, opacity: hidden && !expanded ? 0.7 : 1 }}
    >
      <div className="card-header" style={{ marginBottom: expanded ? 12 : 0, flexWrap: 'nowrap' }}>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-controls={`${sid}-body`}
          style={{ flex: 1, minWidth: 0, justifyContent: 'flex-start', gap: 8, textAlign: 'left', padding: '4px 6px' }}
        >
          <span aria-hidden="true" style={{ width: 10, fontSize: 10, flexShrink: 0 }}>{expanded ? '▼' : '▶'}</span>
          <span style={{ color: 'var(--color-muted)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{index + 1}</span>
          <span className="chip" style={{ padding: '1px 8px', fontSize: 11, flexShrink: 0 }}>{typeLabel(draft.type)}</span>
          <span id={`${sid}-heading`} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 400, color: expanded ? 'var(--color-muted)' : 'var(--color-text)' }}>
            {getSectionPreview({ type: draft.type, data })}
          </span>
        </button>
        <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', flexShrink: 0 }}>
          {jsonEdited && <Badge state="warn">Unknown type</Badge>}
          {hidden && <Badge state="draft">Hidden</Badge>}
          {dirty && <Badge state="warn">Unsaved</Badge>}
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => onMove(index, index - 1)} disabled={busy || index === 0} aria-label={`Move section ${index + 1} up`} title="Move up">↑</button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => onMove(index, index + 1)} disabled={busy || index === count - 1} aria-label={`Move section ${index + 1} down`} title="Move down">↓</button>
          <button type="button" className="btn btn-ghost btn-sm btn-danger-text" onClick={() => onDelete(section.id)} disabled={busy} aria-label={`Delete section ${index + 1}`} title="Delete section">✕</button>
        </span>
      </div>

      <div id={`${sid}-body`} hidden={!expanded}>
        {jsonEdited && (
          <div className="alert alert-warning" role="status" style={{ marginBottom: 12 }}>
            Unknown type “{draft.type}”: it is not one of the twelve types the API knows, so it is
            shown as raw JSON rather than a form. Pick a known type under Section settings to convert it.
          </div>
        )}
        {renderFields()}

        <details style={{ marginTop: 4, marginBottom: 12 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--color-muted)' }}>Section settings</summary>
          <div className="field-row" style={{ marginTop: 10 }}>
            <Field id={`${sid}-type`} label="Type" hint="Changing the type resets its content.">
              <select id={`${sid}-type`} className="select" value={draft.type} onChange={(e) => setType(e.target.value)}>
                {typeOptions.map((t) => <option key={t} value={t}>{typeLabel(t)}</option>)}
              </select>
            </Field>
            <Field id={`${sid}-padding`} label="Padding">
              <select id={`${sid}-padding`} className="select" value={settings.padding || 'normal'} onChange={(e) => setSettings('padding', e.target.value)}>
                <option value="none">None</option>
                <option value="small">Small</option>
                <option value="normal">Normal</option>
                <option value="large">Large</option>
              </select>
            </Field>
          </div>
          <CheckField id={`${sid}-visible`} label="Visible on the page" checked={settings.visible !== false} onChange={(v) => setSettings('visible', v)} />
        </details>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end' }}>
          {dirty && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDraft(pick(section))} disabled={busy}>
              Discard
            </button>
          )}
          <button type="button" className="btn btn-primary btn-sm" onClick={() => onSave(section, draft)} disabled={busy || !dirty}>
            {busy ? 'Saving…' : 'Save section'}
          </button>
        </div>
      </div>

      {picker && <ImagePicker onSelect={onPicked} onClose={() => setPicker(null)} />}
    </article>
  );
}
